/**
 * Composable pour les computed properties des formations
 * Limite: 100 lignes max
 */

import { computed } from 'vue'
import { requiresExteriorSpace } from '../../utils/formationUtils.js'

export function useFormationComputed(getFormValue, formationsData, formationMessages, parseDate, categoriesList) {
  const availableFormations = computed(() => {
    const categorie = getFormValue('formation_categorie')
    const clientType = getFormValue('client_type')
    if (!categorie) return []
    
    let formations = formationsData.value[categorie] || []
    
    if (clientType === 'particulier') {
      formations = formations.filter(f => f.INTER && f.INTER.toUpperCase() === 'OUI')
    }
    
    // Copie avant tri : `sort` mute en place, et sans filtrage préalable on écrirait
    // dans le tableau réactif que ce computed lit (boucle de mise à jour côté watchers).
    return [...formations].sort((a, b) => {
      const orderA = a.ORDER !== undefined && a.ORDER !== null ? a.ORDER : 0
      const orderB = b.ORDER !== undefined && b.ORDER !== null ? b.ORDER : 0
      if (orderA !== orderB) {
        return orderA - orderB
      }
      return a.NOM.localeCompare(b.NOM, 'fr')
    })
  })

  const availableCategories = computed(() => {
    const clientType = getFormValue('client_type')
    const cats = categoriesList.value.filter(cat => (formationsData.value[cat] || []).length > 0)

    if (clientType === 'particulier') {
      return cats.filter(cat => {
        const formations = formationsData.value[cat] || []
        return formations.some(f => f.INTER && f.INTER.toUpperCase() === 'OUI')
      })
    }

    return cats
  })

  // Le catalogue contient des formations homonymes qui ne diffèrent que par la durée
  // (ex. « gestes et postures travail sur écran » en 3H00 et en 7H00) : l'identifiant
  // catalogue prime. Le nom reste le repli pour les demandes créées avant ce champ.
  const selectedFormation = computed(() => {
    const id = getFormValue('formation_catalogue_id')
    if (id) {
      const byId = availableFormations.value.find(f => String(f.ID) === String(id))
      if (byId) return byId
    }
    const nom = getFormValue('formation_nom')
    if (!nom) return null
    return availableFormations.value.find(f => f.NOM === nom)
  })

  const formationMessage = computed(() => {
    if (!selectedFormation.value) return null
    const messageCode = selectedFormation.value.MESSAGE
    return formationMessages.value[messageCode] || null
  })

  const isFormationUnavailable = computed(() => {
    if (!selectedFormation.value) return false
    const dispo = selectedFormation.value.DISPO || ''
    return dispo.toUpperCase().trim() === 'NA'
  })

  const requiresExteriorSpaceForFormation = computed(() => {
    const nom = getFormValue('formation_nom')
    return requiresExteriorSpace(nom)
  })

  return {
    availableFormations,
    availableCategories,
    selectedFormation,
    formationMessage,
    isFormationUnavailable,
    requiresExteriorSpaceForFormation
  }
}
